import { useEffect, useMemo, useState } from 'react';
import { Icon, Platform } from '../../components/Icons.jsx';
import { notify } from '@/lib/notifications';
import { ChipInput } from './shared/ChipInput.jsx';
import { fromBrandForm, isBrandProfileReady, toBrandForm } from './shared/brandFormUtils.js';
import {
  GOAL_OPTIONS,
  TONE_OPTIONS,
  VISUAL_STYLE_OPTIONS,
  LANGUAGE_OPTIONS,
  PLATFORMS,
  PLATFORM_LABELS,
} from './shared/platformLabels.js';
import { api } from '../../services/api/client.js';

const REFERENCE_TYPE_OPTIONS = [
  ['logo', 'Logo'],
  ['product', 'Product image'],
  ['previous_post', 'Previous post'],
  ['style_reference', 'Style reference'],
  ['website_screenshot', 'Website screenshot'],
  ['brand_screenshot', 'Brand screenshot'],
  ['other', 'Other reference'],
];

export function BrandIdentityTab({ form, setForm, onSaved, onAskAI, socialAccounts = [], onConnectPlatform }) {
  const [original, setOriginal] = useState(form);
  const [saving, setSaving] = useState(false);
  const [referenceType, setReferenceType] = useState('style_reference');
  const [referenceUrl, setReferenceUrl] = useState('');
  const [referenceNotes, setReferenceNotes] = useState('');
  const [referenceFile, setReferenceFile] = useState(null);
  const [referenceBusy, setReferenceBusy] = useState(false);

  useEffect(() => {
    setOriginal(form);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(original), [form, original]);
  const ready = isBrandProfileReady(form);

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const goalsArray = useMemo(
    () => String(form.goalsText || '').split(',').map((g) => g.trim()).filter(Boolean),
    [form.goalsText],
  );
  const setGoals = (next) => update('goalsText', next.join(', '));

  const platformsArray = useMemo(
    () => String(form.preferredPlatformsText || '').split(',').map((g) => g.trim()).filter(Boolean),
    [form.preferredPlatformsText],
  );
  const platformPriorityArray = useMemo(
    () => String(form.platformPriorityText || '').split(',').map((g) => g.trim()).filter(Boolean),
    [form.platformPriorityText],
  );
  const togglePlatform = (id) => {
    const next = platformsArray.includes(id)
      ? platformsArray.filter((x) => x !== id)
      : [...platformsArray, id];
    update('preferredPlatformsText', next.join(', '));
  };

  const forbiddenArray = useMemo(
    () => String(form.forbiddenTopicsText || '').split(',').map((g) => g.trim()).filter(Boolean),
    [form.forbiddenTopicsText],
  );
  const setForbidden = (next) => update('forbiddenTopicsText', next.join(', '));
  const sourceStats = useMemo(() => sourceStatsFor(form), [form]);
  const liveSocialStatus = useMemo(() => buildSocialConnectStatus(socialAccounts, platformsArray), [socialAccounts, platformsArray]);

  async function save() {
    if (!ready) {
      notify('brand.missingRequired');
      return;
    }
    try {
      setSaving(true);
      const result = await api.profile.updateBrandProfile({
        ...fromBrandForm(form),
        socialConnectStatus: { ...(form.socialConnectStatus || {}), ...liveSocialStatus },
      });
      const next = toBrandForm(result.brandProfile);
      setForm(next);
      setOriginal(next);
      onSaved?.(result.brandProfile);
      notify('brand.saved');
    } catch (err) {
      notify('brand.saveFailed', { error: err });
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    setForm(original);
  }

  function selectReferenceFile(event) {
    const file = event.target.files?.[0] || null;
    if (file && file.size > 5 * 1024 * 1024) {
      event.target.value = '';
      setReferenceFile(null);
      notify('brand.referenceTooLarge');
      return;
    }
    setReferenceFile(file);
  }

  async function addReference() {
    if (referenceBusy) return;
    const url = referenceUrl.trim();
    if (!referenceFile && !url) {
      notify('brand.referenceMissing');
      return;
    }
    try {
      setReferenceBusy(true);
      const payload = {
        type: referenceType,
        notes: referenceNotes.trim() || null,
        url: referenceFile ? undefined : url,
      };
      if (referenceFile) {
        payload.fileName = referenceFile.name;
        payload.mimeType = referenceFile.type;
        payload.dataUrl = await readFileAsDataUrl(referenceFile);
      }
      const result = await api.profile.addBrandReference(payload);
      const next = toBrandForm(result.brandProfile);
      setForm(next);
      setOriginal(next);
      onSaved?.(result.brandProfile);
      setReferenceUrl('');
      setReferenceNotes('');
      setReferenceFile(null);
      notify('brand.referenceSaved', { description: referenceToastText(result.reference) });
    } catch (err) {
      notify('brand.referenceSaveFailed', { error: err });
    } finally {
      setReferenceBusy(false);
    }
  }

  const descCount = String(form.businessDescription || '').length;
  const sectionStatus = useMemo(() => buildSectionStatus(form), [form]);
  const missingRequired = sectionStatus.filter((s) => s.required && !s.ready);
  const [openSection, setOpenSection] = useState(() => missingRequired[0]?.id || '');

  const toneLabel = optionLabel(TONE_OPTIONS, form.toneOfVoice);
  const visualLabel = optionLabel(VISUAL_STYLE_OPTIONS, form.visualStyle);
  const languageLabel = optionLabel(LANGUAGE_OPTIONS, form.language);
  const platformPreview = platformsArray.length
    ? platformsArray.map((id) => PLATFORM_LABELS[id] || id).slice(0, 3).join(', ')
    : 'No preferred channels';
  const goalsPreview = goalsArray.length
    ? goalsArray.map((value) => optionLabel(GOAL_OPTIONS, value)).slice(0, 3).join(', ')
    : 'No goals selected';
  const guardrailsPreview = form.contentPillars?.length
    ? `${form.contentPillars.length} pillars set`
    : forbiddenArray.length
      ? `${forbiddenArray.length} blocked ${forbiddenArray.length === 1 ? 'topic' : 'topics'}`
      : 'No guardrails added';
  const sourceFor = (field) => sourceMetaFor(form, field);

  return (
    <div className="cp-brand-editor">
      <header className="cp-brand-head">
        <div>
          <div className="settings-section-label">Brand inputs</div>
          <h3>{missingRequired.length ? `${missingRequired.length} required ${missingRequired.length === 1 ? 'input' : 'inputs'} missing` : 'Business brain is ready'}</h3>
          <p>This profile is the source of truth for strategy, posts, images, assistant actions, and recommendations.</p>
        </div>
        <button className="btn btn-secondary" onClick={() => onAskAI?.({ scope: 'content-plan.brand' })}>
          <Icon name="sparkles" size={13} /> Ask AI
        </button>
      </header>

      <div className="cp-brand-progress" aria-label="Brand input coverage" style={{ '--brand-step-count': sectionStatus.length }}>
        {sectionStatus.map((section) => (
          <span
            key={section.id}
            className={'cp-brand-progress-step ' + (section.ready ? 'is-ready ' : '') + (section.empty ? 'is-empty ' : '') + (section.required && !section.ready ? 'needs-input' : '')}
            title={`${section.title}: ${section.status}`}
          />
        ))}
      </div>

      <BusinessBrainSummary form={form} sourceStats={sourceStats} socialStatus={liveSocialStatus} />

      <div className="cp-brand-list">
        <BrandSection
          id="basics"
          title="Business"
          description="What the company does"
          preview={compact(form.businessName || form.productOrService || form.businessDescription, 'Needs business context')}
          section={sectionStatus.find((s) => s.id === 'basics')}
          open={openSection === 'basics'}
          onToggle={setOpenSection}
          onAskAI={onAskAI}
        >
          <div className="cp-form-grid">
            <Field label="Business name" source={sourceFor('businessName')}>
              <input className="input form-input" value={form.businessName} onChange={(e) => update('businessName', e.target.value)} placeholder="Acme Studio" />
            </Field>
            <Field label="Product or service" source={sourceFor('productOrService')}>
              <input className="input form-input" value={form.productOrService} onChange={(e) => update('productOrService', e.target.value)} placeholder="What you sell" />
            </Field>
          </div>
          <Field label="Business description" required hint={`${descCount}/500 characters. Short context is enough; AI fills the rest from behavior.`} source={sourceFor('businessDescription')}>
            <textarea
              className="input textarea form-input cp-textarea-locked"
              value={form.businessDescription}
              onChange={(e) => update('businessDescription', e.target.value.slice(0, 500))}
              rows={3}
              placeholder="A boutique design studio helping early-stage SaaS founders ship a credible brand in 30 days."
            />
          </Field>
        </BrandSection>

        <BrandSection
          id="audience"
          title="Audience"
          description="Who AI speaks to"
          preview={compact(form.targetAudience, 'Audience needed')}
          section={sectionStatus.find((s) => s.id === 'audience')}
          open={openSection === 'audience'}
          onToggle={setOpenSection}
          onAskAI={onAskAI}
        >
          <Field label="Target audience" required source={sourceFor('targetAudience')}>
            <textarea className="input textarea form-input cp-textarea-locked" value={form.targetAudience} onChange={(e) => update('targetAudience', e.target.value)} rows={3} placeholder="Solo SaaS founders raising pre-seed, $0-500k ARR" />
          </Field>
          <Field label="Buyer pains" source={sourceFor('buyerPains')}>
            <ChipInput value={form.buyerPains} onChange={(v) => update('buyerPains', v)} placeholder="Add pain and press Enter" />
          </Field>
          <Field label="Objections" source={sourceFor('objections')}>
            <ChipInput value={form.objections} onChange={(v) => update('objections', v)} placeholder="e.g. too expensive, no time" />
          </Field>
        </BrandSection>

        <BrandSection
          id="offer"
          title="Offer"
          description="What AI should position or sell"
          preview={compact(form.offer, 'No offer added')}
          section={sectionStatus.find((s) => s.id === 'offer')}
          open={openSection === 'offer'}
          onToggle={setOpenSection}
          onAskAI={onAskAI}
        >
          <Field label="Your offer" source={sourceFor('offer')}>
            <textarea className="input textarea form-input cp-textarea-locked" value={form.offer} onChange={(e) => update('offer', e.target.value)} rows={3} placeholder="Brand sprint package: landing + visual system in 2 weeks" />
          </Field>
        </BrandSection>

        <BrandSection
          id="goals"
          title="Strategy"
          description="Positioning, goals, and conversion logic"
          preview={compact(form.positioning, goalsPreview)}
          section={sectionStatus.find((s) => s.id === 'goals')}
          open={openSection === 'goals'}
          onToggle={setOpenSection}
          onAskAI={onAskAI}
        >
          <Field label="Goals" hint="Optional. AI can infer defaults, or you can steer it here." source={sourceFor('goals')}>
            <div className="cp-chip-toggle-row">
              {GOAL_OPTIONS.map((opt) => {
                const on = goalsArray.includes(opt.value);
                return <button type="button" key={opt.value} onClick={() => setGoals(on ? goalsArray.filter((x) => x !== opt.value) : [...goalsArray, opt.value])} className={'chip cp-chip-toggle ' + (on ? 'chip-active' : '')}>{opt.label}</button>;
              })}
            </div>
          </Field>
          <Field label="Positioning" source={sourceFor('positioning')}>
            <textarea className="input textarea form-input cp-textarea-locked" value={form.positioning} onChange={(e) => update('positioning', e.target.value)} rows={3} placeholder="The credible choice for founders who need premium output without agency drag." />
          </Field>
          <Field label="Unique selling points" source={sourceFor('uniqueSellingPoints')}>
            <ChipInput value={form.uniqueSellingPoints} onChange={(v) => update('uniqueSellingPoints', v)} placeholder="Add USP and press Enter" />
          </Field>
          <Field label="Conversion actions" source={sourceFor('conversionActions')}>
            <ChipInput value={form.conversionActions} onChange={(v) => update('conversionActions', v)} placeholder="e.g. book a call, start checkout" />
          </Field>
        </BrandSection>

        <BrandSection
          id="voice"
          title="Tone"
          description="Voice, words, and compliance"
          preview={`${toneLabel} voice / ${form.wordsToAvoid?.length || 0} avoid words`}
          section={sectionStatus.find((s) => s.id === 'voice')}
          open={openSection === 'voice'}
          onToggle={setOpenSection}
          onAskAI={onAskAI}
        >
          <div className="cp-form-grid">
            <Field label="Tone of voice" source={sourceFor('toneOfVoice')}>
              <select className="input select form-input" value={form.toneOfVoice} onChange={(e) => update('toneOfVoice', e.target.value)}>
                {TONE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </Field>
          </div>
          {form.toneOfVoice === 'custom' && (
            <Field label="Custom tone notes" source={sourceFor('customToneDescription')}>
              <textarea className="input textarea form-input cp-textarea-locked" value={form.customToneDescription} onChange={(e) => update('customToneDescription', e.target.value)} rows={3} placeholder="Direct, founder-led, no jargon. Use 1st person." />
            </Field>
          )}
          <Field label="Words to use" source={sourceFor('wordsToUse')}>
            <ChipInput value={form.wordsToUse} onChange={(v) => update('wordsToUse', v)} placeholder="Add preferred word" />
          </Field>
          <Field label="Words to avoid" source={sourceFor('wordsToAvoid')}>
            <ChipInput value={form.wordsToAvoid} onChange={(v) => update('wordsToAvoid', v)} placeholder="Add banned word" />
          </Field>
        </BrandSection>

        <BrandSection
          id="guardrails"
          title="Guardrails"
          description="Themes to repeat, topics to avoid"
          preview={guardrailsPreview}
          section={sectionStatus.find((s) => s.id === 'guardrails')}
          open={openSection === 'guardrails'}
          onToggle={setOpenSection}
          onAskAI={onAskAI}
        >
          <Field label="Content pillars" hint="Optional. Add 3-5 themes if the brand needs strict repeatable lanes." source={sourceFor('contentPillars')}>
            <ChipInput value={form.contentPillars} onChange={(v) => update('contentPillars', v)} placeholder="Add a pillar and press Enter" />
          </Field>
          <Field label="Forbidden topics" hint="Optional. AI avoids these topics when present." source={sourceFor('forbiddenTopics')}>
            <ChipInput value={forbiddenArray} onChange={setForbidden} placeholder="e.g. competitor names, discount messaging" />
          </Field>
          <Field label="Compliance boundaries" source={sourceFor('complianceBoundaries')}>
            <ChipInput value={form.complianceBoundaries} onChange={(v) => update('complianceBoundaries', v)} placeholder="e.g. no medical claims, no guarantees" />
          </Field>
        </BrandSection>

        <BrandSection
          id="visual"
          title="Visual"
          description="Image style, colors, references"
          preview={`${visualLabel} / ${form.referenceAssets?.length || form.referenceAssetIds?.length || 0} refs`}
          section={sectionStatus.find((s) => s.id === 'visual')}
          open={openSection === 'visual'}
          onToggle={setOpenSection}
          onAskAI={onAskAI}
        >
          <div className="cp-form-grid">
            <Field label="Visual style" source={sourceFor('visualStyle')}>
              <select className="input select form-input" value={form.visualStyle} onChange={(e) => update('visualStyle', e.target.value)}>
                {VISUAL_STYLE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </Field>
            <Field label="Preferred image mood" source={sourceFor('preferredImageMood')}>
              <input className="input form-input" value={form.preferredImageMood} onChange={(e) => update('preferredImageMood', e.target.value)} placeholder="Premium, calm, high contrast" />
            </Field>
          </div>
          <Field label="Brand colors" source={sourceFor('brandColors')}>
            <ChipInput value={form.brandColors} onChange={(v) => update('brandColors', v)} placeholder="Add color or hex" />
          </Field>
          <Field label="Image composition hints" source={sourceFor('imageCompositionHints')}>
            <ChipInput value={form.imageCompositionHints} onChange={(v) => update('imageCompositionHints', v)} placeholder="e.g. centered subject, roomy margins" />
          </Field>
          <Field label="Avoid visuals" source={sourceFor('avoidVisuals')}>
            <ChipInput value={form.avoidVisuals} onChange={(v) => update('avoidVisuals', v)} placeholder="e.g. cheesy stock photos" />
          </Field>
          <div className="cp-form-grid">
            <Field label="Logo asset ID" source={sourceFor('logoAssetId')}>
              <input className="input form-input" value={form.logoAssetId} onChange={(e) => update('logoAssetId', e.target.value)} placeholder="Optional media asset id" />
            </Field>
            <Field label="Reference asset IDs" source={sourceFor('referenceAssetIds')}>
              <ChipInput value={form.referenceAssetIds} onChange={(v) => update('referenceAssetIds', v)} placeholder="Add asset id" />
            </Field>
          </div>
          <ReferenceDerivedList references={form.referenceAssets} />
          <BrandReferenceUploader
            type={referenceType}
            onTypeChange={setReferenceType}
            url={referenceUrl}
            onUrlChange={setReferenceUrl}
            notes={referenceNotes}
            onNotesChange={setReferenceNotes}
            file={referenceFile}
            onFileChange={selectReferenceFile}
            onAdd={addReference}
            busy={referenceBusy}
          />
        </BrandSection>

        <BrandSection
          id="distribution"
          title="Distribution"
          description="Baseline channels, language, timezone"
          preview={`${platformPreview} / ${languageLabel}`}
          section={sectionStatus.find((s) => s.id === 'distribution')}
          open={openSection === 'distribution'}
          onToggle={setOpenSection}
          onAskAI={onAskAI}
        >
          <Field label="Preferred platforms" hint="Optional. AI adapts each channel automatically." source={sourceFor('preferredPlatforms')}>
            <div className="cp-chip-toggle-row">
              {PLATFORMS.map((id) => {
                const on = platformsArray.includes(id);
                return <button type="button" key={id} onClick={() => togglePlatform(id)} className={'chip cp-chip-toggle ' + (on ? 'chip-active' : '')}>{PLATFORM_LABELS[id]}</button>;
              })}
            </div>
          </Field>
          <Field label="Platform priority" source={sourceFor('platformPriority')}>
            <div className="cp-chip-toggle-row">
              {PLATFORMS.map((id) => {
                const on = platformPriorityArray.includes(id);
                const next = on ? platformPriorityArray.filter((x) => x !== id) : [...platformPriorityArray, id];
                return <button type="button" key={id} onClick={() => update('platformPriorityText', next.join(', '))} className={'chip cp-chip-toggle ' + (on ? 'chip-active' : '')}>{PLATFORM_LABELS[id]}</button>;
              })}
            </div>
          </Field>
          <Field label="Per-platform tone" hint="One per line, e.g. linkedin: professional founder-led" source={sourceFor('perPlatformTone')}>
            <textarea className="input textarea form-input" value={form.perPlatformToneText} onChange={(e) => update('perPlatformToneText', e.target.value)} rows={3} placeholder="linkedin: founder-led and precise" />
          </Field>
          <Field label="Per-platform format preferences" hint="One per line, e.g. instagram: carousel, image_post" source={sourceFor('perPlatformFormatPreferences')}>
            <textarea className="input textarea form-input" value={form.perPlatformFormatPreferencesText} onChange={(e) => update('perPlatformFormatPreferencesText', e.target.value)} rows={3} placeholder="instagram: carousel, image_post" />
          </Field>
          <PlatformConnectionCtas platforms={platformsArray} socialStatus={liveSocialStatus} onConnect={onConnectPlatform} />
          <WebsiteDerivedPanel form={form} />
          <div className="cp-form-grid">
            <Field label="Language" source={sourceFor('language')}>
              <select className="input select form-input" value={form.language} onChange={(e) => update('language', e.target.value)}>
                {LANGUAGE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </Field>
            <Field label="Timezone" hint="IANA tz name" source={sourceFor('timezone')}>
              <input className="input form-input" value={form.timezone} onChange={(e) => update('timezone', e.target.value)} placeholder="Europe/Kyiv" />
            </Field>
          </div>
        </BrandSection>
      </div>

      {dirty && (
        <div className="cp-sticky-savebar">
          <button className="btn btn-secondary" onClick={cancel} disabled={saving}>
            <Icon name="refresh" size={13} /> Cancel
          </button>
          <button className="btn btn-primary" onClick={save} disabled={saving || !ready}>
            <Icon name="check" size={13} /> {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      )}
    </div>
  );
}

function BrandSection({ id, title, description, preview, section, open, onToggle, onAskAI, children }) {
  const status = section?.status || (section?.ready ? 'Done' : 'Needs input');
  const needsInput = section?.required && !section?.ready;
  const statusClass = needsInput ? 'needs-input' : section?.empty ? 'is-empty' : section?.ready ? 'is-done' : '';
  return (
    <section className={'cp-brand-section ' + (open ? 'is-open ' : '') + (section?.ready ? 'is-ready ' : '') + (section?.empty ? 'is-empty ' : '') + (needsInput ? 'needs-input' : '')} data-section={id}>
      <button type="button" className="cp-brand-section-trigger" onClick={() => onToggle(open ? '' : id)} aria-expanded={open}>
        <span className={'cp-node-dot ' + (section?.ready ? 'is-ready ' : '') + (section?.empty ? 'is-empty' : '')} />
        <span className="cp-module-title-block cp-brand-title-block">
          <span className="cp-brand-title-line">
            <strong>{title}</strong>
            <span className={'cp-brand-state ' + statusClass}>
              {section?.ready && <Icon name="check" size={11} />}
              {status}
            </span>
          </span>
          <span>{description}</span>
        </span>
        <span className="cp-brand-preview"><span>{preview}</span></span>
        {needsInput ? <span className="cp-brand-row-action needs-input">Fill</span> : <span className="cp-brand-row-action is-empty" aria-hidden="true" />}
        <Icon name={open ? 'chev_u' : 'chev_d'} size={14} />
      </button>
      {open && (
        <div className="cp-brand-section-body">
          {children}
          <div className="cp-assist-strip">
            <button className="btn btn-ghost btn-sm" onClick={() => onAskAI?.({ scope: `content-plan.brand.${id}` })}>
              <Icon name="sparkles" size={12} /> Ask AI to improve
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function buildSectionStatus(form) {
  const has = (value) => String(value || '').trim().length > 0;
  const hasPillars = Array.isArray(form.contentPillars) && form.contentPillars.length > 0;
  const hasForbidden = has(form.forbiddenTopicsText);
  const hasDistribution = has(form.language) && has(form.timezone);
  const hasGoals = String(form.goalsText || '').split(',').some((g) => g.trim());
  const hasStrategy = hasGoals || has(form.positioning) || form.uniqueSellingPoints?.length || form.conversionActions?.length;
  const hasOffer = has(form.offer);
  const hasVisual = has(form.visualStyle) || form.brandColors?.length || form.referenceAssetIds?.length || form.referenceAssets?.length;
  return [
    { id: 'basics', title: 'Business', required: true, ready: has(form.businessDescription), status: has(form.businessDescription) ? 'Done' : 'Needs input' },
    { id: 'audience', title: 'Audience', required: true, ready: has(form.targetAudience), status: has(form.targetAudience) ? 'Done' : 'Needs input' },
    { id: 'offer', title: 'Offer', ready: hasOffer, empty: !hasOffer, status: hasOffer ? 'Set' : 'Not set' },
    { id: 'goals', title: 'Strategy', ready: hasStrategy, empty: !hasStrategy, status: hasStrategy ? 'Set' : 'Not set' },
    { id: 'voice', title: 'Tone', ready: has(form.toneOfVoice), status: has(form.toneOfVoice) ? 'Done' : 'Needs review' },
    { id: 'guardrails', title: 'Guardrails', ready: hasPillars || hasForbidden, empty: !(hasPillars || hasForbidden), status: hasPillars || hasForbidden ? 'Set' : 'Not set' },
    { id: 'visual', title: 'Visual', ready: hasVisual, empty: !hasVisual, status: hasVisual ? 'Set' : 'Not set' },
    { id: 'distribution', title: 'Distribution', ready: hasDistribution, empty: !hasDistribution, status: hasDistribution ? 'Done' : 'Not set' },
  ];
}

function Field({ label, required, hint, source, children }) {
  return (
    <div className="form-field cp-field">
      <label>
        {label}
        {required && <span className="cp-required"> *</span>}
        {source && <SourceBadge meta={source} />}
      </label>
      {children}
      {hint && <div className="helper">{hint}</div>}
    </div>
  );
}

function BusinessBrainSummary({ form, sourceStats, socialStatus }) {
  const connectedCount = Object.values(socialStatus || {}).filter((item) => item?.connected).length;
  return (
    <div className="cp-brain-summary">
      <div>
        <span className="settings-section-label">Business brain</span>
        <strong>{sourceStats.reviewCount ? `${sourceStats.reviewCount} fields need review` : 'Confirmed source of truth'}</strong>
        <p>{form.websiteUrl ? `Website context: ${form.websiteUrl}` : 'Add website context to improve strategy and image prompts.'}</p>
      </div>
      <div className="cp-brain-metrics">
        <Metric label="AI suggested" value={sourceStats.aiCount} />
        <Metric label="Website" value={sourceStats.websiteCount} />
        <Metric label="References" value={sourceStats.referenceCount} />
        <Metric label="Connected" value={connectedCount} />
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return <span><strong>{value}</strong>{label}</span>;
}

function SourceBadge({ meta }) {
  const source = sourceLabel(meta?.source);
  const confidence = Number(meta?.confidence);
  const needsReview = meta?.needsReview === true || (Number.isFinite(confidence) && confidence < 0.72);
  return (
    <span className={'cp-source-badge ' + (needsReview ? 'needs-review' : '')}>
      {source}{Number.isFinite(confidence) ? ` ${Math.round(confidence * 100)}%` : ''}{needsReview ? ' review' : ''}
    </span>
  );
}

function PlatformConnectionCtas({ platforms, socialStatus, onConnect }) {
  const items = platforms.length ? platforms : ['linkedin', 'instagram'];
  return (
    <div className="cp-platform-cta-grid">
      {items.map((platform) => {
        const connected = socialStatus?.[platform]?.connected;
        return (
          <div key={platform} className="cp-platform-cta">
            <span><Platform name={platform} size={15} /> {PLATFORM_LABELS[platform] || platform}</span>
            {connected ? <strong>Connected</strong> : <button type="button" className="btn btn-secondary btn-sm" onClick={() => onConnect?.(platform)}>Connect</button>}
          </div>
        );
      })}
    </div>
  );
}

function WebsiteDerivedPanel({ form }) {
  if (!form.websiteUrl && !form.websiteAnalysis) return null;
  const title = form.websiteAnalysis?.title || form.websiteAnalysis?.ogTitle || 'Website analyzed';
  const headings = Array.isArray(form.websiteAnalysis?.headings) ? form.websiteAnalysis.headings.slice(0, 4) : [];
  return (
    <div className="cp-derived-panel">
      <strong>Website-derived fields</strong>
      <span>{form.websiteUrl || title}</span>
      {headings.length > 0 && <p>{headings.join(' / ')}</p>}
    </div>
  );
}

function ReferenceDerivedList({ references }) {
  const items = Array.isArray(references) ? references : [];
  if (!items.length) return null;
  return (
    <div className="cp-derived-panel">
      <strong>Reference-derived fields</strong>
      <div className="cp-reference-card-grid">
        {items.filter((item) => item.publicUrl).slice(0, 4).map((item) => (
          <div key={item.assetId || item.publicUrl} className="cp-reference-card">
            <img src={item.publicUrl} alt={`${item.type || 'Reference'} preview`} loading="lazy" />
            <small>{item.analysisStatus === 'analyzed' ? 'Analyzed' : 'Saved'}</small>
          </div>
        ))}
      </div>
      {items.slice(0, 5).map((item) => <span key={item.assetId || item.publicUrl}>{item.type || 'reference'}{item.promptGuidance || item.notes ? `: ${item.promptGuidance || item.notes}` : ''}</span>)}
    </div>
  );
}

function BrandReferenceUploader({ type, onTypeChange, url, onUrlChange, notes, onNotesChange, file, onFileChange, onAdd, busy }) {
  return (
    <div className="cp-reference-uploader">
      <div>
        <strong>Add visual reference</strong>
        <p>Upload a logo, post, screenshot, product image, or style sample. Dathent stores reusable prompt guidance.</p>
      </div>
      <div className="cp-form-grid">
        <select className="input select form-input" value={type} onChange={(event) => onTypeChange(event.target.value)} disabled={busy}>
          {REFERENCE_TYPE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <input className="input form-input" type="url" value={url} onChange={(event) => onUrlChange(event.target.value)} placeholder="Public image URL" disabled={busy || Boolean(file)} />
      </div>
      <input className="input form-input" type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={onFileChange} disabled={busy} />
      <BrandReferencePreview file={file} url={url} />
      <textarea className="input textarea form-input" value={notes} onChange={(event) => onNotesChange(event.target.value)} rows={2} placeholder="I like this style because..." disabled={busy} />
      <button type="button" className="btn btn-secondary btn-sm" onClick={onAdd} disabled={busy || (!file && !url.trim())}>
        {busy ? 'Saving reference...' : 'Add reference'}
      </button>
    </div>
  );
}

function BrandReferencePreview({ file, url }) {
  const [previewUrl, setPreviewUrl] = useState('');
  useEffect(() => {
    if (!file) {
      setPreviewUrl('');
      return undefined;
    }
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);
  const src = previewUrl || String(url || '').trim();
  if (!src) return null;
  return <img className="cp-reference-preview-img" src={src} alt="Selected visual reference preview" loading="lazy" />;
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(new Error('Unable to read reference file'));
    reader.readAsDataURL(file);
  });
}

function referenceToastText(reference) {
  if (reference?.analysisStatus === 'analyzed') return 'Visual guidance analyzed and linked to Brand Profile.';
  if (reference?.promptGuidance) return 'Reusable visual guidance saved to Brand Profile.';
  return 'Reference saved to Brand Profile.';
}

function sourceStatsFor(form) {
  const values = Object.values(form.sourceMetadata || {});
  return values.reduce((stats, meta) => {
    const source = meta?.source;
    if (source === 'website') stats.websiteCount += 1;
    if (source === 'uploaded_reference') stats.referenceCount += 1;
    if (source === 'ai_inferred' || source === 'assistant_suggestion') stats.aiCount += 1;
    const confidence = Number(meta?.confidence);
    if (meta?.needsReview === true || (Number.isFinite(confidence) && confidence < 0.72)) stats.reviewCount += 1;
    return stats;
  }, { websiteCount: 0, referenceCount: 0, aiCount: 0, reviewCount: 0 });
}

function sourceMetaFor(form, field) {
  return form.sourceMetadata?.[field] || null;
}

function sourceLabel(source) {
  if (source === 'manual') return 'Manual';
  if (source === 'website') return 'Website';
  if (source === 'uploaded_reference') return 'Reference';
  if (source === 'user_answer') return 'User answer';
  if (source === 'assistant_suggestion') return 'AI suggestion';
  if (source === 'ai_inferred') return 'AI inferred';
  return 'Unknown source';
}

function buildSocialConnectStatus(accounts, platforms) {
  const status = {};
  const wanted = platforms.length ? platforms : PLATFORMS;
  for (const platform of wanted) {
    const matches = (accounts || []).filter((account) => normalizePlatform(account.platform) === platform);
    status[platform] = { connected: matches.some((account) => account.status === 'connected' || account.status === 'ready'), count: matches.length };
  }
  return status;
}

function normalizePlatform(value) {
  return String(value || '').toLowerCase() === 'twitter' ? 'x' : String(value || '').toLowerCase();
}

function compact(value, fallback) {
  const text = String(value || '').trim();
  if (!text) return fallback;
  return text.length > 58 ? `${text.slice(0, 55)}...` : text;
}

function optionLabel(options, value) {
  return options.find((opt) => opt.value === value)?.label || value || 'Auto';
}
